import { tr } from '@/lib/i18n-core';
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { get, put } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { tenantUsesArabic } from '@/lib/locale-fit';
import { money } from '@/lib/utils';
import { labelSettingsFrom, printLabels, probePrintBridge } from '@/lib/print-bridge';
import { RejectionReasonsCard } from './RejectionReasonsCard';
import { Alert, Button, Card, CardContent, CardHeader, CardTitle, Checkbox, Field, Input, SkeletonText } from '@/components/ui/primitives';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { useToast } from '@/components/ui/toast';
import { formatMoneyInput, minorFromInput } from '@/components/MoneyInput';

type Choice<T extends string> = { value: T; label: string };

interface LabSettingsValues {
  separateVerifier: boolean;
  qcWindowHours: number;
  criticalEscalationMinutes: number;
  criticalEscalationPhone: string | null;
  billOverlappingPanels: boolean;
  accessionPerOrder: boolean;
  releaseRequiresPayment: boolean;
  analyserResultsEditable: boolean;
  statCollectionMinutes: number;
  reportReadySms: 'whole_order' | 'each_release' | null;
  accessionMode: 'per_test' | 'per_tube';
  tubeSuffix: 'letter' | 'numeric';
  homeCollectionEnabled: boolean | null;
  collectionIdentityCheck: 'off' | 'on';
  collectRequiresPayment: boolean;
  analyserOverride: 'technician' | 'with_reason' | 'with_approval' | 'verifier_only';
  labelReprintApproval: boolean;
  labelShowDob: boolean;
  labelShowNationalId: boolean;
  reportHeaderMode: 'letterhead' | 'preprinted';
  preprintedTopMm: number;
  labReportLanguage: 'en' | 'ar' | 'both' | null;
  instrumentLogDays: number;
  labelWidthMm: number | null;
  labelHeightMm: number | null;
  labelCommandLang: string | null;
  printBridgePort: number | null;
  publicPortalUrl: string | null;
}

interface LabSettingsResponse {
  values: LabSettingsValues;
  homeCollectionFee: { chargeCode: string; priceMinor: number; warning: string | null };
}

interface Form {
  separateVerifier: boolean;
  billOverlappingPanels: boolean;
  releaseRequiresPayment: boolean;
  collectRequiresPayment: boolean;
  labelReprintApproval: boolean;
  labelShowDob: boolean;
  labelShowNationalId: boolean;
  qcWindowHours: string;
  criticalEscalationMinutes: string;
  statCollectionMinutes: string;
  preprintedTopMm: string;
  instrumentLogDays: string;
  escalationPhone: string;
  reportReadySms: string;
  accessionMode: string;
  tubeSuffix: string;
  homeCollectionEnabled: string;
  collectionIdentityCheck: string;
  analyserOverride: string;
  reportHeaderMode: string;
  labReportLanguage: string;
  labelWidthMm: string;
  labelHeightMm: string;
  labelCommandLang: string;
  printBridgePort: string;
  publicPortalUrl: string;
}

const LIMITS = {
  qcWindowHours: { min: 1, max: 168 },
  criticalEscalationMinutes: { min: 5, max: 1440 },
  statCollectionMinutes: { min: 1, max: 240 },
  preprintedTopMm: { min: 0, max: 120 },
  instrumentLogDays: { min: 7, max: 3650 },
  labelWidthMm: { min: 20, max: 120 },
  labelHeightMm: { min: 10, max: 120 },
  printBridgePort: { min: 1024, max: 65535 },
};

const DEFAULT = '';

const REPORT_SMS: Choice<string>[] = [
  { value: DEFAULT, label: 'Default for this facility' },
  { value: 'whole_order', label: 'Once, when the whole order is released' },
  { value: 'each_release', label: 'Each release ("part of your report is ready")' },
];
const ACCESSION_MODES: Choice<string>[] = [
  { value: 'per_test', label: 'One accession per test' },
  { value: 'per_tube', label: 'One accession per tube (tests in one tube share it)' },
];
const TUBE_SUFFIXES: Choice<string>[] = [
  { value: 'letter', label: 'Letter (-A, -B)' },
  { value: 'numeric', label: 'Two digits (01, 02) — for analysers that read numbers only' },
];
const HOME: Choice<string>[] = [
  { value: DEFAULT, label: 'Default for this facility' },
  { value: 'on', label: 'On' },
  { value: 'off', label: 'Off' },
];
const IDENTITY: Choice<string>[] = [
  { value: 'off', label: 'Off' },
  { value: 'on', label: 'On — confirm each patient at the draw' },
];
const OVERRIDE: Choice<string>[] = [
  { value: 'technician', label: 'Technicians may overtype' },
  { value: 'with_reason', label: 'Technicians give a reason' },
  { value: 'with_approval', label: 'Reason and a second person approves' },
  { value: 'verifier_only', label: 'Only a verifier may change it' },
];
const HEADER: Choice<string>[] = [
  { value: 'letterhead', label: 'Print our letterhead' },
  { value: 'preprinted', label: 'Pre-printed stationery (leave the top blank)' },
];
const PRINTER_LANGUAGE: Choice<string>[] = [
  { value: 'TSPL', label: 'TSPL (TSC and similar label printers)' },
  { value: 'ESCPOS', label: 'ESC/POS (receipt printers)' },
];
const LANGUAGE: Choice<string>[] = [
  { value: DEFAULT, label: 'Default (both in the Gulf, else the hospital language)' },
  { value: 'en', label: 'English' },
  { value: 'ar', label: 'Arabic' },
  { value: 'both', label: 'English and Arabic' },
];

function formFrom(values: LabSettingsValues): Form {
  const label = labelSettingsFrom({
    labelWidthMm: values.labelWidthMm,
    labelHeightMm: values.labelHeightMm,
    labelCommandLang: values.labelCommandLang,
    printBridgePort: values.printBridgePort,
  });
  return {
    labelWidthMm: String(label.widthMm),
    labelHeightMm: String(label.heightMm),
    labelCommandLang: label.language,
    printBridgePort: String(label.port),
    publicPortalUrl: values.publicPortalUrl ?? '',
    separateVerifier: values.separateVerifier,
    billOverlappingPanels: values.billOverlappingPanels,
    releaseRequiresPayment: values.releaseRequiresPayment,
    collectRequiresPayment: values.collectRequiresPayment,
    labelReprintApproval: values.labelReprintApproval,
    labelShowDob: values.labelShowDob,
    labelShowNationalId: values.labelShowNationalId,
    qcWindowHours: String(values.qcWindowHours),
    criticalEscalationMinutes: String(values.criticalEscalationMinutes),
    statCollectionMinutes: String(values.statCollectionMinutes),
    preprintedTopMm: String(values.preprintedTopMm),
    instrumentLogDays: String(values.instrumentLogDays),
    escalationPhone: values.criticalEscalationPhone ?? '',
    reportReadySms: values.reportReadySms ?? DEFAULT,
    accessionMode: values.accessionMode,
    tubeSuffix: values.tubeSuffix,
    homeCollectionEnabled: values.homeCollectionEnabled === null ? DEFAULT : values.homeCollectionEnabled ? 'on' : 'off',
    collectionIdentityCheck: values.collectionIdentityCheck,
    analyserOverride: values.analyserOverride,
    reportHeaderMode: values.reportHeaderMode,
    labReportLanguage: values.labReportLanguage ?? DEFAULT,
  };
}

export function payloadFrom(form: Form): Record<string, unknown> {
  return {
    separateVerifier: form.separateVerifier,
    billOverlappingPanels: form.billOverlappingPanels,
    accessionMode: form.accessionMode,
    accessionPerOrder: form.accessionMode === 'per_tube',
    tubeSuffix: form.tubeSuffix,
    releaseRequiresPayment: form.releaseRequiresPayment,
    collectRequiresPayment: form.collectRequiresPayment,
    analyserOverride: form.analyserOverride,
    analyserResultsEditable: form.analyserOverride !== 'verifier_only',
    labelReprintApproval: form.labelReprintApproval,
    labelShowDob: form.labelShowDob,
    labelShowNationalId: form.labelShowNationalId,
    qcWindowHours: Number(form.qcWindowHours),
    criticalEscalationMinutes: Number(form.criticalEscalationMinutes),
    statCollectionMinutes: Number(form.statCollectionMinutes),
    preprintedTopMm: Number(form.preprintedTopMm),
    instrumentLogDays: Number(form.instrumentLogDays),
    criticalResultEscalationPhone: form.escalationPhone.trim() || null,
    collectionIdentityCheck: form.collectionIdentityCheck,
    reportHeaderMode: form.reportHeaderMode,
    ...(form.reportReadySms ? { reportReadySms: form.reportReadySms } : {}),
    ...(form.homeCollectionEnabled ? { homeCollectionEnabled: form.homeCollectionEnabled === 'on' } : {}),
    labReportLanguage: form.labReportLanguage || null,
    labelWidthMm: Number(form.labelWidthMm),
    labelHeightMm: Number(form.labelHeightMm),
    labelCommandLang: form.labelCommandLang === 'ESCPOS' ? 'ESCPOS' : 'TSPL',
    printBridgePort: Number(form.printBridgePort),
    publicPortalUrl: form.publicPortalUrl.trim() || null,
  };
}

function within(text: string, limits: { min: number; max: number }): boolean {
  const parsed = Number(text);
  return text.trim() !== '' && Number.isInteger(parsed) && parsed >= limits.min && parsed <= limits.max;
}

function CheckRow({ checked, onChange, label, help }: { checked: boolean; onChange: (checked: boolean) => void; label: string; help?: string }) {
  return <Checkbox checked={checked} onCheckedChange={onChange} label={label} description={help} />;
}

function ChoiceField({ label, hint, value, choices, onChange }: { label: string; hint?: string; value: string; choices: Choice<string>[]; onChange: (value: string) => void }) {
  return (
    <Field label={label} hint={hint}>
      <ChoiceButtons layout="cards" columns={2} value={value} onChange={onChange} options={choices} />
    </Field>
  );
}

function NumberField({ label, hint, value, limits, onChange }: { label: string; hint?: string; value: string; limits: { min: number; max: number }; onChange: (value: string) => void }) {
  return (
    <Field label={label} hint={hint} error={within(value, limits) ? undefined : `Enter a whole number from ${limits.min} to ${limits.max}`}>
      <Input type="number" min={limits.min} max={limits.max} value={value} onChange={(e) => onChange(e.target.value)} />
    </Field>
  );
}

export function LabManagerSettings() {
  const toast = useToast();
  const { can, user } = useAuth();
  const languageChoices = tenantUsesArabic(user) ? LANGUAGE : LANGUAGE.filter((choice) => choice.value !== 'ar' && choice.value !== 'both');
  const queryClient = useQueryClient();
  const settings = useQuery({ queryKey: ['lab-settings'], queryFn: () => get<LabSettingsResponse>('/lab/settings') });
  const [form, setForm] = useState<Form | null>(null);
  const [fee, setFee] = useState('');
  const [probe, setProbe] = useState<string | null>(null);

  useEffect(() => {
    if (!settings.data) return;
    setForm(formFrom(settings.data.values));
    setFee(formatMoneyInput(settings.data.homeCollectionFee.priceMinor));
  }, [settings.data]);

  const save = useMutation({
    mutationFn: () => put<LabSettingsResponse>('/lab/settings', payloadFrom(form as Form)),
    onSuccess: () => {
      toast.success(tr('lab', 'laboratory_settings_saved'));
      void queryClient.invalidateQueries({ queryKey: ['lab-settings'] });
      void queryClient.invalidateQueries({ queryKey: ['tenant-settings'] });
    },
    onError: (error) => toast.error(tr('lab', 'could_not_save_the_settings'), describeError(error)),
  });

  const saveFee = useMutation({
    mutationFn: () => put<LabSettingsResponse>('/lab/settings/home-fee', { priceMinor: minorFromInput(fee) }),
    onSuccess: () => {
      toast.success(tr('lab', 'home_collection_fee_saved'));
      void queryClient.invalidateQueries({ queryKey: ['lab-settings'] });
    },
    onError: (error) => toast.error(tr('lab', 'could_not_save_the_fee'), describeError(error)),
  });

  if (settings.error) return <Alert variant="destructive">{describeError(settings.error)}</Alert>;
  if (!form || !settings.data) return <SkeletonText lines={2} />;

  const set = (patch: Partial<Form>) => setForm({ ...form, ...patch });
  const valid = (Object.keys(LIMITS) as (keyof typeof LIMITS)[]).every((key) => within(form[key], LIMITS[key]));
  const feeValid = fee.trim() !== '' && minorFromInput(fee) >= 0;
  const testLabel = async () => {
    const printer = labelSettingsFrom(payloadFrom(form));
    setProbe('Looking for the print bridge…');
    const health = await probePrintBridge(printer.port);
    if (!health) {
      setProbe(`No print bridge answered on 127.0.0.1:${printer.port}. Start it on this computer and try again.`);
      return;
    }
    const result = await printLabels([{ barcode: 'TEST-LABEL', topText: 'HIS TEST' }], printer);
    setProbe(result.ok ? `Test label sent to ${result.printer ?? health.defaultPrinter ?? 'the default printer'}.` : `The bridge refused the job: ${result.error}`);
  };

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">{tr('lab', 'collection_and_accession')}</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4">
          <ChoiceField label={tr('lab', 'accession_numbers')} value={form.accessionMode} choices={ACCESSION_MODES} onChange={(value) => set({ accessionMode: value })} />
          {form.accessionMode === 'per_tube' && <ChoiceField label={tr('lab', 'tube_suffix')} value={form.tubeSuffix} choices={TUBE_SUFFIXES} onChange={(value) => set({ tubeSuffix: value })} />}
          <ChoiceField
            label={tr('lab', 'positive_patient_identification_at_collection')}
            hint={tr('lab', 'the_collector_confirms_each_patient_by')}
            value={form.collectionIdentityCheck}
            choices={IDENTITY}
            onChange={(value) => set({ collectionIdentityCheck: value })}
          />
          <ChoiceField label={tr('lab', 'home_sample_collection')} value={form.homeCollectionEnabled} choices={HOME} onChange={(value) => set({ homeCollectionEnabled: value })} />
          <CheckRow
            checked={form.collectRequiresPayment}
            onChange={(checked) => set({ collectRequiresPayment: checked })}
            label={tr('lab', 'collect_a_walk_in_specimen_only_once_its_bill')}
            help="Never applies to ward, ER, insured or credit/staff/free orders. A supervisor can collect with a reason."
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">{tr('lab', 'results_and_release')}</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4">
          <ChoiceField label={tr('lab', 'changing_a_value_that_came_from_an_analyser')} value={form.analyserOverride} choices={OVERRIDE} onChange={(value) => set({ analyserOverride: value })} />
          <CheckRow checked={form.separateVerifier} onChange={(checked) => set({ separateVerifier: checked })} label={tr('lab', 'the_person_who_entered_a_result_cannot_verify_it')} />
          <CheckRow
            checked={form.releaseRequiresPayment}
            onChange={(checked) => set({ releaseRequiresPayment: checked })}
            label={tr('lab', 'hold_release_of_a_report_until_its_bill_is_paid')}
            help="Ward and ER orders are never held. An order with no bill and no credit, staff or free marking is held. A supervisor can release with a reason."
          />
          <CheckRow
            checked={form.billOverlappingPanels}
            onChange={(checked) => set({ billOverlappingPanels: checked })}
            label={tr('lab', 'bill_a_panel_even_when_every_test_in_it_is')}
          />
          <ChoiceField
            label={tr('lab', 'report_ready_text_message_to_the_patient')}
            hint={tr('lab', 'ward_and_er_patients_are_not_texted')}
            value={form.reportReadySms}
            choices={REPORT_SMS}
            onChange={(value) => set({ reportReadySms: value })}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">{tr('lab', 'timing_and_critical_results')}</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4">
          <NumberField
            label={tr('lab', 'stat_order_not_collected_after_minutes')}
            hint={tr('lab', 'the_worklist_flags_a_stat_order_whose_specimen')}
            value={form.statCollectionMinutes}
            limits={LIMITS.statCollectionMinutes}
            onChange={(value) => set({ statCollectionMinutes: value })}
          />
          <NumberField label={tr('lab', 'qc_failure_blocks_result_entry_for_hours')} value={form.qcWindowHours} limits={LIMITS.qcWindowHours} onChange={(value) => set({ qcWindowHours: value })} />
          <NumberField
            label={tr('lab', 'critical_result_escalation_minutes')}
            value={form.criticalEscalationMinutes}
            limits={LIMITS.criticalEscalationMinutes}
            onChange={(value) => set({ criticalEscalationMinutes: value })}
          />
          <Field label={tr('lab', 'critical_result_escalation_number')} hint={tr('lab', 'paged_when_no_ordering_doctor_attending_doctor')}>
            <Input value={form.escalationPhone} placeholder={tr('lab', '03xxxxxxxxx')} onChange={(e) => set({ escalationPhone: e.target.value })} />
          </Field>
          <NumberField label={tr('lab', 'keep_analyser_message_logs_for_days')} value={form.instrumentLogDays} limits={LIMITS.instrumentLogDays} onChange={(value) => set({ instrumentLogDays: value })} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">{tr('lab', 'labels_and_printed_reports')}</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4">
          <CheckRow checked={form.labelShowDob} onChange={(checked) => set({ labelShowDob: checked })} label={tr('lab', 'print_the_date_of_birth_on_labels_age_otherwise')} />
          <CheckRow checked={form.labelShowNationalId} onChange={(checked) => set({ labelShowNationalId: checked })} label={tr('lab', 'print_the_national_id_on_labels')} />
          <CheckRow
            checked={form.labelReprintApproval}
            onChange={(checked) => set({ labelReprintApproval: checked })}
            label={tr('lab', 'a_label_reprint_needs_a_supervisors_approval')}
            help="People allowed to reprint directly are not asked"
          />
          <ChoiceField label={tr('lab', 'report_language')} value={form.labReportLanguage} choices={languageChoices} onChange={(value) => set({ labReportLanguage: value })} />
          <Field
            label={tr('lab', 'web_address_patients_open_reports_at')}
            hint={tr('lab', 'put_in_the_report_ready_text_message_leave')}
            error={form.publicPortalUrl.trim() && !/^https?:\/\/\S+$/.test(form.publicPortalUrl.trim()) ? 'Start with https://' : undefined}
          >
            <Input value={form.publicPortalUrl} placeholder="https://reports.example.com" onChange={(e) => set({ publicPortalUrl: e.target.value })} />
          </Field>
          <ChoiceField label={tr('lab', 'report_header')} value={form.reportHeaderMode} choices={HEADER} onChange={(value) => set({ reportHeaderMode: value })} />
          {form.reportHeaderMode === 'preprinted' && (
            <NumberField label={tr('lab', 'blank_space_at_the_top_mm')} value={form.preprintedTopMm} limits={LIMITS.preprintedTopMm} onChange={(value) => set({ preprintedTopMm: value })} />
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">{tr('lab', 'label_printer')}</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <NumberField label={tr('lab', 'label_width_mm')} value={form.labelWidthMm} limits={LIMITS.labelWidthMm} onChange={(value) => set({ labelWidthMm: value })} />
          <NumberField label={tr('lab', 'label_height_mm')} value={form.labelHeightMm} limits={LIMITS.labelHeightMm} onChange={(value) => set({ labelHeightMm: value })} />
          <ChoiceField label={tr('lab', 'printer_language')} value={form.labelCommandLang} choices={PRINTER_LANGUAGE} onChange={(value) => set({ labelCommandLang: value })} />
          <NumberField
            label={tr('lab', 'print_bridge_port')}
            hint={tr('lab', 'the_label_print_bridge_runs_on_each_workstation')}
            value={form.printBridgePort}
            limits={LIMITS.printBridgePort}
            onChange={(value) => set({ printBridgePort: value })}
          />
          <div className="flex flex-wrap items-center gap-2 sm:col-span-2">
            <Button variant="outline" onClick={testLabel}>
              {tr('lab', 'send_a_test_label_from_this_computer')}
            </Button>
            {probe && <span className="text-sm text-muted-foreground">{probe}</span>}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">{tr('lab', 'home_sample_collection_fee')}</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-[minmax(0,16rem)_auto] sm:items-end">
          {settings.data.homeCollectionFee.warning && <Alert variant="warning" className="sm:col-span-2">{settings.data.homeCollectionFee.warning}</Alert>}
          <Field label={tr('lab', 'fee_for_a_home_visit_x', { chargeCode: settings.data.homeCollectionFee.chargeCode })} hint={tr('lab', 'now_x', { priceMinor: money(settings.data.homeCollectionFee.priceMinor) })}>
            <Input type="text" dir="ltr" inputMode="decimal" value={fee} onChange={(e) => setFee(e.target.value)} />
          </Field>
          <Button variant="outline" onClick={() => saveFee.mutate()} loading={saveFee.isPending} disabled={!feeValid}>
            {tr('lab', 'save_fee')}
          </Button>
        </CardContent>
      </Card>

      {can('lab.master.manage') && <RejectionReasonsCard />}

      <div className="lg:col-span-2">
        <Button onClick={() => save.mutate()} loading={save.isPending} disabled={!valid}>
          {tr('lab', 'save_laboratory_settings')}
        </Button>
      </div>
    </div>
  );
}
